import { useState } from "react";
import { Link } from "react-router-dom";
import { useDateFormatter, useTranslations } from "../../contexts/Translation";
import { useImprovementGoal } from "../../contexts/ImprovementGoal";
import SystemMessage from "../../components/SystemMessage/Index";
import Section from "../Section";
import ImprovementGoalForm from "../Forms/ImprovementGoalForm";
import GoalConclusionForm from "../Forms/GoalConclusionForm";
import Comments from "../Comments/List";
import { CreateImprovementGoalLink } from "../Survey/CreateImprovementGoalLink";
import { categoryLabel } from "../Dashboard/labels";
import ConclusionFlag from "./ConclusionFlag";
import GoalHero from "./GoalHero";
import GoalPhases from "./GoalPhases";
import SurveyTable from "./SurveyTable";
import GoalAside from "./GoalAside";
import { goalPhases } from "./phases";
import { surveyRows } from "./goalSurveys";
import styles from "./Details.module.css";

const ImprovementGoalDetails = () => {
  const t = useTranslations("improvementGoals");
  const d = useDateFormatter();
  const { goal, improvementData, originalSurvey, recentSurveys } = useImprovementGoal();
  const [isEdit, setIsEdit] = useState(false);
  const [journalKey, setJournalKey] = useState(0);

  if (!goal) {
    return <SystemMessage message='loading' />;
  }

  const isOwner = !!goal.isOwner;
  const categories = goal.categories.filter(Boolean);
  const { phases, progress, conclusionOpen } = goalPhases(goal);
  const rows = surveyRows({ goal, improvementData, originalSurvey, recentSurveys });
  const latest = rows.length > 1 ? rows[rows.length - 1] : null;

  return (
    <>
      <Section>
        <GoalHero
          goal={goal}
          backTo='/improvements'
          backLabel={t("backToGoals")}
          facts={
            <>
              <li>
                <Link className={styles.factLink} to={`/survey/${goal.originalSurvey}`}>
                  {t("originalSurvey")}
                  {improvementData?.surveyName ? `: ${improvementData.surveyName}` : ""}
                </Link>
              </li>
              {goal.registered && <li>{t("startLabel", d(goal.registered))}</li>}
              <li>{t("endLabel", d(goal.endDate))}</li>
            </>
          }
          isOwner={!!goal.canEdit}
          isEdit={isEdit}
          onToggleEdit={() => setIsEdit(!isEdit)}
        />
        <GoalPhases phases={phases} progress={progress} conclusionOpen={conclusionOpen} />
      </Section>
      <Section span={8} className={styles.main}>
        {isEdit ? (
          <div className={styles.card}>
            <ImprovementGoalForm isEdit onSuccess={() => setIsEdit(false)} />
          </div>
        ) : (
          <div className={styles.details}>
            <section className={styles.card}>
              <h2 className={styles.cardHeading}>{t("goalCardHeading")}</h2>
              <p className={styles.preWrap}>{goal.goal}</p>
              <p className={styles.text}>
                <strong>{t("improvementCategories")}: </strong>
                {categories.length > 0 ? categories.map((c) => t(categoryLabel(c))).join(", ") : t("noSelectedCategories")}
              </p>
            </section>
            <section className={styles.card}>
              <h2 className={styles.cardHeading}>{t("situationCardHeading")}</h2>
              <p className={styles.preWrap}>{goal.situation}</p>
            </section>
            <section className={styles.card}>
              <h2 className={styles.cardHeading}>{t("planCardHeading")}</h2>
              <p className={styles.preWrap}>{goal.plan}</p>
            </section>
          </div>
        )}

        <SurveyTable rows={rows} categories={categories} />

        <section className={styles.card}>
          {conclusionOpen && isEdit ? (
            <GoalConclusionForm onSuccess={() => setIsEdit(false)} />
          ) : (
            <>
              <h2 className={styles.cardHeading}>
                {t("conclusionHeading")}
                {conclusionOpen && <ConclusionFlag isSuccess={goal.isSuccess} successLabel={t("success")} failureLabel={t("fail")} />}
              </h2>
              <p className={styles.preWrap}>{conclusionOpen ? goal.result || t("emptyConclusion") : t("patienceConclusion")}</p>
            </>
          )}
          {conclusionOpen && goal.isSuccess && isOwner && latest && <CreateImprovementGoalLink text={t("createNewGoal")} surveyId={latest.id} />}
        </section>

        <section className={styles.card}>
          <h2 className={styles.cardHeading}>{t("journalHeading")}</h2>
          <Comments key={journalKey} goalId={goal.id} />
        </section>
      </Section>
      <Section span={4}>
        <aside>
          <GoalAside goal={goal} isOwner={isOwner} onCoachComment={() => setJournalKey((k) => k + 1)} />
        </aside>
      </Section>
    </>
  );
};

export default ImprovementGoalDetails;
